import {
  cloneElement,
  isValidElement,
  useId,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";

interface FieldProps {
  /** Caption shown above the control. Becomes a real <label htmlFor=…> when
   * the child is a single labelable element (input/select/textarea). */
  label: string;
  children: ReactNode;
  /** Optional explicit id — overrides the useId fallback. */
  id?: string;
  /** Optional style override for the wrapper. */
  style?: CSSProperties;
  /** Optional style override for the label/caption. */
  labelStyle?: CSSProperties;
}

/**
 * Shared admin form field — generates a useId and injects it into a single
 * labelable child via React.cloneElement, then renders a `<label htmlFor=…>`
 * for proper screen-reader association (WCAG 3.3.2).
 *
 * For composite children (e.g. a checkbox already wrapped in its own
 * `<label>`), renders the caption as a non-associated `<span>` to avoid
 * nested-label invalid HTML; the inner element retains its own association.
 *
 * Usage:
 *   <Field label="Yield">
 *     <input value={yield} onChange={…} />
 *   </Field>
 *
 *   <Field label="Required" labelStyle={{...}}>
 *     <label><input type="checkbox" /> required</label>
 *   </Field>
 */
export default function Field({ label, children, id: explicitId, style, labelStyle }: FieldProps) {
  const generatedId = useId();
  const child = isValidElement(children) ? (children as ReactElement<any>) : null;
  const tag = child && typeof child.type === "string" ? child.type : null;
  const isLabelable = tag === "input" || tag === "select" || tag === "textarea";
  const childPropsId = (child?.props as any)?.id;
  const inputId = explicitId ?? childPropsId ?? generatedId;
  const enhancedChild =
    isLabelable && !childPropsId && child
      ? cloneElement(child, { id: inputId } as any)
      : children;

  return (
    <div style={style ?? defaultFieldStyle}>
      {label &&
        (isLabelable ? (
          <label htmlFor={inputId} style={labelStyle ?? defaultLabelStyle}>
            {label}
          </label>
        ) : (
          <span style={labelStyle ?? defaultLabelStyle}>{label}</span>
        ))}
      {enhancedChild}
    </div>
  );
}

const defaultFieldStyle: CSSProperties = {
  display: "grid",
  gap: 6,
  marginBottom: 12,
};

const defaultLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--muted)",
};
